// src/api/onboarding-api.ts
//
// API helpers + types for the customer onboarding pipeline.
//
import {
  apiFetch,
  AuthExpiredError,
  getStoredToken,
  notifyAuthExpired,
} from "./api-fetch";
import { buildApiUrl, buildWsUrl } from "./url-route";
import type { RunCompleteness } from "./run-completeness";
import type { RunSeries, SeriesRebaseline } from "./series-rebaseline";


// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

export interface StageDefinition {
  number: number;
  title: string;
  short_description?: string;
  subtitle?: string;
  estimated_duration?: string;
  duration_estimate?: string;
  what_happens: string[];
  next_action_label?: string;
}

export type UseCase = "starting_fresh" | "replacing" | "expanding" | "fixing" | "justifying";

export const USE_CASES: { key: UseCase; label: string }[] = [
  { key: "starting_fresh", label: "Starting fresh" },
  { key: "replacing", label: "Replacing" },
  { key: "expanding", label: "Expanding" },
  { key: "fixing", label: "Fixing" },
  { key: "justifying", label: "Justifying" },
];

/** A buyer role. Generated personas (#136) carry id, goal and use_case;
 *  legacy target users have only label and description. */
export interface TargetUser {
  id?: string;
  label: string;
  description: string;
  goal?: string;
  use_case?: UseCase;
}
export type Persona = TargetUser;

/** A sentence from the brand's site or documents, verbatim, and where. */
export interface EvidenceQuote {
  quote: string;
  source?: string;
  url?: string;
  source_label?: string;
}

export interface ReasoningVariable {
  id: string;
  claim: string;
  label?: string;
  rationale?: string;
  why_it_matters?: string;
  // Per-score rubric anchors that tell the scorer exactly what each level
  // looks like. Stage 2 generates them (#136); legacy frameworks may lack them.
  score_2?: string; // what an explicit (score 2) response looks like
  score_1?: string; // what an implied / vague (score 1) response looks like
  score_0?: string; // what an absent (score 0) response looks like
  /** One example answer sentence per level ("0", "1", "2"). */
  examples?: Record<string, string>;
  evidence?: EvidenceQuote[];
}

export type QueryCategory =
  | "direct" | "problem" | "feature" | "comparison" | "economics" | "integration";

export interface FrameworkQuery {
  id: string;
  category: QueryCategory | string;
  text: string;
  // Persona that generated the query (e.g. "practice_owner"). Optional and
  // captured on raw responses so per-persona breakdowns are possible later.
  persona?: string;
  // Reasoning-variable id(s) this query is designed to surface. Stage 2
  // generates them; Stage 3 edits them. They define a variable's "applicable
  // cells" in the Smoke calibration, so an id here that no current variable
  // carries makes that variable's whole row blind (zero cells + a Coverage
  // flag) rather than raising anything. Empty is legal and means holistic
  // coverage — every variable applicable to every response.
  variable_ids?: string[];
  /** Persona id(s) asking it (#136). Kept equal to [persona_id] (#137). */
  persona_ids?: string[];
  /** The persona (target_users[].id) who meets this scenario (#137). */
  persona_id?: string;
  /** Where the persona stands in this scenario (#137). */
  use_case?: UseCase | string;
}

/** The battery's targets and reviewer marks (#137), framework_json.battery. */
export interface BatteryBlock {
  version?: number;
  form_targets?: Record<string, number>;
  /** "That role does not have this conversation": the generator leaves it empty. */
  excluded_cells?: { persona_id: string; form: string; reason?: string; by?: string; at?: string }[];
  /** A yellow lint rule accepted with a reason, bound to its findings. */
  acknowledgements?: { rule: string; fingerprint: string; reason: string; by?: string; at?: string }[];
  /** The category-context classifier's verdicts; written by the server. */
  context_checks?: Record<string, { present: boolean; context: string; reason: string; text_sha: string }>;
}

/**
 * The reasoning-variable fields the judge actually sees. Mirrors
 * SCORING_BASIS_FIELDS in backend/app/services/scoring_service.py, which is the
 * authority - the backend re-derives this on save and writes the result to the
 * MANIFEST. This copy exists only so the console can warn BEFORE the save,
 * while the operator can still reconsider. Keep the two lists identical.
 */
const SCORING_BASIS_FIELDS = ["claim", "score_2", "score_1", "score_0", "examples"] as const;

/** examples count only when present (SCORING_BASIS_OPTIONAL_FIELDS). */
function basisValue(v: any, field: string): string {
  if (field !== "examples") return String(v?.[field] ?? "");
  const ex = v?.examples;
  if (!ex || typeof ex !== "object") return "";
  const present = ["0", "1", "2"].filter((k) => String(ex[k] ?? "").trim());
  return present.length ? JSON.stringify(present.map((k) => [k, String(ex[k]).trim()])) : "";
}

export interface ScoringBasisDiff {
  /** Variables whose judged text changed, with which fields moved. */
  changed: { id: string; fields: string[] }[];
  added: string[];
  removed: string[];
  /** True when anything the judge reads moved. */
  affectsStoredScores: boolean;
}

/**
 * What a framework edit does to the text scores were judged against.
 *
 * Clarifying a rationale, a target user or a query leaves every stored score
 * exactly as valid as it was, and must not nag the operator. Rewriting a
 * score_2 anchor does not: the lineage was judged against the old anchor, so
 * those scores answer a question the rubric no longer asks. A removed variable
 * counts too - its stored scores are orphaned from the rubric.
 */
export function diffScoringBasis(previous: Framework | undefined, revised: Framework): ScoringBasisDiff {
  const basis = (fw?: Framework) => {
    const map = new Map<string, Record<string, string>>();
    for (const v of fw?.reasoning_variables ?? []) {
      const fields: Record<string, string> = {};
      for (const f of SCORING_BASIS_FIELDS) fields[f] = basisValue(v, f);
      map.set(String(v?.id ?? ""), fields);
    }
    return map;
  };

  const before = basis(previous);
  const after = basis(revised);

  const changed: { id: string; fields: string[] }[] = [];
  for (const [id, fields] of before) {
    const now = after.get(id);
    if (!now) continue;
    const moved = SCORING_BASIS_FIELDS.filter((f) => fields[f] !== now[f]);
    if (moved.length) changed.push({ id, fields: [...moved] });
  }
  const added = [...after.keys()].filter((id) => !before.has(id));
  const removed = [...before.keys()].filter((id) => !after.has(id));

  return {
    changed,
    added,
    removed,
    affectsStoredScores: changed.length > 0 || added.length > 0 || removed.length > 0,
  };
}

export interface MisidentificationGate {
  name: string;
  descriptor?: string;
  location?: string;
  domain?: string;
  how_to_tell?: string;
}

export interface Framework {
  target_users: TargetUser[];
  reasoning_variables: ReasoningVariable[];
  queries: FrameworkQuery[];
  name_variants: string[];
  misidentification?: MisidentificationGate[];
  /** The question battery's targets and marks (#137). */
  battery?: BatteryBlock;
  /** Present on frameworks Stage 2's pipeline generated (#136). */
  generation?: {
    pipeline: string;
    model: string;
    counts: Record<string, number>;
    steps: Record<string, number>;
    generation_id: number;
  };
}

export interface ScoringSummary {
  mention_rate?: number;
  top5_rate?: number;
  primary_rec_rate?: number;
  number_one_rate?: number;
  per_model?: Record<string, {
    mention_rate?: number;
    top5_rate?: number;
    primary_rec_rate?: number;
    number_one_rate?: number;
  }>;
  per_reasoning_variable?: Record<string, number>;
  responses_scored?: number;
  scoring_cost_usd_est?: number;
}

export interface ValidationResult {
  sample_size?: number;
  mention_positive_count?: number;
  control_count?: number;
  cohen_kappa?: number | null;
  needs_manual_review?: boolean;
  notes?: string;
}

export interface GptZeroDiagnostic {
  triggered?: boolean;
  triggered_models?: string[];
  web_retrieval_test?: Record<string, {
    queries_tested?: number;
    mentions_low_context?: number;
    mention_rate_low_context?: number;
  }>;
  interpretation?: string;
  cost_usd_est?: number;
}

export interface BaselineReport {
  headline?: {
    mention_rate?: number;
    top5_rate?: number;
    primary_rec_rate?: number;
    number_one_rate?: number;
  };
  per_model_breakdown?: Record<string, any>;
  variable_scores?: Record<string, number>;
  name_variant_analysis?: Record<string, number>;
  weak_variables?: string[];
  strong_variables?: string[];
  generated_at?: string;
  run_id?: number;
}

export interface BlueprintAction {
  id: string;
  wave: string;
  title: string;
  description: string;
  deployment_surface: string;
  target_variables: string[];
  effort: "Low" | "Medium" | "High";
  expected_lift: string;
}

export interface Blueprint {
  summary?: string;
  wave_1?: BlueprintAction[];
  wave_2?: BlueprintAction[];
  wave_3?: BlueprintAction[];
  generated_at?: string;
}

export interface ScheduledRemeasurement {
  id: number;
  label: string;
  scheduled_for: string;
  status: string;
}

/** Lightweight summary of one measurement run — what shows in the Stage 11 list. */
export interface RunSummary {
  id: number;
  run_sequence_number: number | null;
  run_label: string | null;
  /** Optional free-text display name. Falls back to "Run #N" when null. */
  run_name: string | null;
  status: "pending" | "running" | "completed" | "cancelled" | "failed";
  triggered_by: string | null;
  batch_config: {
    queries: number | null;
    reps: number | null;
    models: string[];
    total_calls: number | null;
  };
  progress: {
    completed: number;
    total: number;
  };
  cost_usd: number;
  cancellation_requested: boolean;
  error_message: string | null;
  /** The admin's standing decision about this run's place in the client
   *  Performance chart: "auto" = the rules decide (a real measurement label
   *  whose scored-row count clears the floor), "exclude" = never plotted,
   *  "include" = plotted even below the floor. */
  series_inclusion?: "auto" | "include" | "exclude";
  /** Display-only: true = kept for audit but hidden from the Performance chart.
   *  The legacy boolean view of series_inclusion === "exclude". */
  excluded_from_series: boolean;
  /** Whether this run is actually plotted right now, with the reason when not:
   *  "admin_excluded" | "preflight_label" | "below_scored_floor" |
   *  "not_a_candidate" (not completed, or never scored). */
  series_eligible?: boolean;
  series_exclusion_reason?: string | null;
  /** Scored rows an "auto" run needs for this customer, and what this run has. */
  series_scored_floor?: number;
  scored_count?: number;
  /** A pass has FINISHED and its numbers are stored. NOT "is it scored right
   *  now" — that is `scoring.in_flight`. The two were conflated, which is how
   *  a run being batch-scored for six hours read as never scored. */
  has_scoring: boolean;
  has_validation: boolean;
  has_report: boolean;
  /** Stage-5 scoring as it actually stands. `in_flight` is the identical
   *  predicate /scoring/state returns as `scoring_in_flight` — the backend
   *  computes both from one function, so they cannot disagree. */
  scoring?: RunScoringStatus;
  /** The transport the active (or running) pass was SENT on. null = not
   *  recorded, never "the default". Promoted out of `scoring` because it is
   *  what explains the run's judge bill. */
  scoring_mode?: ScoringMode | null;
  /** Rubric AND judge of the run's ACTIVE scoring pass, or of the pass running
   *  right now (null if neither). Together they identify one pass, which is
   *  what pins the per-variable viewer and the exports to it. Rubric alone does
   *  not: a run judged twice on the same rubric (run 29: opus and sonnet) would
   *  blend both judges. */
  rubric_version: string | null;
  scorer_model?: string | null;
  headline_metrics: {
    mention_rate: number;
    top5_rate: number;
    primary_rec_rate: number;
    number_one_rate: number;
  } | null;
  /** Incomplete-run verdict (excluded + failed calls over the threshold, by
   *  lane). null/absent = the run has no score rows yet. */
  completeness?: RunCompleteness | null;
  /** Series segment: battery version, the rebaseline that opened it, and
   *  whether this run is that rebaseline's new baseline. */
  series?: RunSeries | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
}

/** How a scoring pass was SENT: "batch" is the Anthropic Message Batches API at
 *  half of standard prices with results when the batch ends; "direct" is one
 *  synchronous request per response at full price. Same judge, same rubric,
 *  same scores either way — this is a cost-and-latency choice only. */
export type ScoringMode = "batch" | "direct";

/** Where a run stands with Stage-5 scoring.
 *
 *  not_started  no pass recorded and none running.
 *  in_flight    a judge pass is running right now.
 *  completed    a pass finished and is the run's active pass.
 *  adopted      the active pass was adopted, not re-judged (no judge ran).
 *  failed       the last attempt failed, or the scoring/report pipeline errored
 *               without ever storing a summary.
 *  cancelled    the last attempt was cancelled at a batch boundary; its partial
 *               scores survive. */
export interface RunScoringStatus {
  state:
    | "not_started" | "in_flight" | "completed"
    | "adopted" | "failed" | "cancelled";
  /** The same boolean /scoring/state returns as `scoring_in_flight`. */
  in_flight: boolean;
  /** null = not recorded. A pass rebuilt from its stored score rows cannot
   *  know its transport — nothing about it survives in a score row. */
  mode: ScoringMode | null;
  scorer_model: string | null;
  rubric_version: string | null;
  /** "live" | "adopted" | "rebuilt" — how the active pass got here. */
  summary_origin: string | null;
  /** When the running pass began (in_flight only). */
  started_at: string | null;
  /** When the finished pass completed. */
  finished_at: string | null;
  /** Progress of the pass in flight; null in every other state — a finished
   *  pass's row count is `responses_scored` on the pass, which is
   *  authoritative. The run list does not count responses for a run whose
   *  cheap fields already settle the verdict, so reporting a number here off a
   *  settled run would mean the two endpoints disagreeing about one run. */
  scored_rows: number | null;
  expected_rows: number | null;
  /** Set only in the "failed" state. */
  error_message: string | null;
}

/** Full per-run detail — used by the Compare page lookup. */
export interface RunDetail {
  id: number;
  customer_id: number;
  run_sequence_number: number | null;
  run_label: string | null;
  triggered_by: string | null;
  status: string;
  batch_config: Record<string, any>;
  progress_completed: number;
  progress_total: number;
  cost_usd: number;
  scoring_summary: ScoringSummary | Record<string, never>;
  validation: ValidationResult | Record<string, never>;
  baseline_report: BaselineReport | Record<string, never>;
  error_message: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
}

/** One metric's baseline/candidate/delta in a per-model comparison. Any field
 *  may be null when the model is missing from one side of the comparison. */
export interface PerModelMetricDelta {
  baseline: number | null;
  candidate: number | null;
  delta: number | null;
}

/** One lane's model-version comparability between two runs (#114).
 *
 *  `comparable` is false when the lane ran on different model versions and the
 *  change is not explained. `reason` says which kind:
 *    "unlogged_model_change"      — the version moved with no logged pin move.
 *    "operator_version_override"  — one of the runs was pinned to another
 *                                   model by hand (a continuity read, a
 *                                   baseline reproduction), so it was never
 *                                   meant to be a point on the client's series.
 *  `pin_move_logged` true means the change IS explained — a forced, reactive
 *  platform move — and the delta stands, annotated. */
export interface LaneVersionComparability {
  baseline_version: string | null;
  candidate_version: string | null;
  changed: boolean;
  comparable: boolean;
  reason: "unlogged_model_change" | "operator_version_override" | null;
  pin_move_logged?: boolean;
  provenance?: "recorded" | "incomplete";
  /** The version change was made by this series rebaseline (logged, planned). */
  rebaseline_id?: number;
}

/** Per-model collection health for a run — lets the UI distinguish a real 0%
 *  ("answered, no mention") from a hollow 0% ("returned nothing / errored"). */
export interface ResponseHealth {
  total: number;
  answered: number;
  empty: number;
  errored: number;
  sample_error: string | null;
}

/** Comparison payload returned by compareRuns(). */
export interface RunComparison {
  baseline_run: {
    id: number;
    sequence_number: number | null;
    label: string | null;
    completed_at: string | null;
    batch_config: Record<string, any>;
    completeness?: RunCompleteness | null;
  };
  candidate_run: {
    id: number;
    sequence_number: number | null;
    label: string | null;
    completed_at: string | null;
    batch_config: Record<string, any>;
    completeness?: RunCompleteness | null;
  };
  // `delta` is NULL when the two runs did not blend the same set of lanes. A
  // headline is an average over whatever contributed to it, so subtracting two
  // that were built from different lanes measures a coverage change, not a
  // change in how AI answers. Null means "not comparable" and must render as
  // such; rendering it as 0 would read as "measured, and nothing moved".
  headline_deltas: Record<string, {
    baseline: number;
    candidate: number;
    delta: number | null;
    comparable?: boolean;
  }>;
  // Which lanes fed each side of the headline comparison, so the UI can say
  // WHY a delta is missing instead of showing a blank.
  headline_lane_sets?: {
    comparable: boolean;
    baseline_lanes: string[];
    candidate_lanes: string[];
    added_since_baseline: string[];
    dropped_since_baseline: string[];
    blend_excluded: string[];
  };
  // Per-model lift. `in_baseline`/`in_candidate` flag whether the model ran in
  // each run; when a model is absent from the baseline (e.g. gptweb/Gemini added
  // later) baseline/delta are null so the UI shows "new this run" rather than a
  // misleading full-value lift.
  per_model_deltas: Record<string, {
    in_baseline?: boolean;
    in_candidate?: boolean;
    candidate_health?: ResponseHealth | null;
    baseline_health?: ResponseHealth | null;
    // The model-version story for this lane (#114). A lane that ran in BOTH
    // runs can still be non-comparable: if it ran on a different model version
    // and no pin move explains the change, its deltas are null. `reason`
    // separates that from "new this run" — the two look identical otherwise,
    // and calling a model change "new" is wrong in both directions.
    version?: LaneVersionComparability | null;
    mention_rate: PerModelMetricDelta;
    top5_rate: PerModelMetricDelta;
    primary_rec_rate: PerModelMetricDelta;
    number_one_rate: PerModelMetricDelta;
  }>;
  // Model-version comparability across the two runs (#114).
  model_versions?: {
    comparable: boolean;
    lanes: Record<string, LaneVersionComparability>;
    unexplained_lanes: string[];
    changed_lanes: string[];
    provenance: "full" | "partial" | "none";
    headline_comparable: boolean;
    headline_unexplained_lanes: string[];
  };
  /** Whether both runs asked the same question battery (#137). When
   *  `comparable` is false every delta is null. */
  battery?: {
    comparable: boolean;
    /** Null when either run predates snapshots (its battery is not known). */
    changed: boolean | null;
    known: boolean;
    covered_by: number[];
    reason: "battery_changed" | null;
    baseline_hash: string | null;
    candidate_hash: string | null;
  };
  rv_deltas: Array<{
    rv_id: string;
    claim: string;
    baseline: number;
    candidate: number;
    /** Null across a battery change no rebaseline covers (#137). */
    delta: number | null;
  }>;
  variant_deltas: Array<{
    variant: string;
    baseline_count: number;
    candidate_count: number;
    baseline_share: number;
    candidate_share: number;
    delta_count: number;
  }>;
  /** Series rebaselines between the two runs. `crosses` false = same segment. */
  rebaseline?: {
    crosses: boolean;
    reason: "crosses_rebaseline" | null;
    crossed: SeriesRebaseline[];
    baseline_battery_version: number;
    candidate_battery_version: number;
  };
}

export interface CustomerDetail {
  id: number;
  brand_name: string;
  website_url: string;
  competitors: string[];
  one_liner: string;
  /** #136: confirmed at intake. */
  category?: string;
  brief?: string;
  current_stage: number;
  status: string;
  framework: Framework;
  framework_approved: boolean;
  framework_approved_at?: string | null;
  /**
   * Whether this brand's measurement identity has ever been locked, i.e. it has
   * scores in a lineage judged against an approved rubric. Unlocking clears
   * framework_approved and framework_approved_at, so only this stays true while
   * a revision is in flight - which is when the screen needs to know.
   */
  framework_ever_locked?: boolean;
  measurement_run_id?: number | null;
  monitoring_run_id?: number | null;
  monitoring_username?: string | null;
  scoring_summary?: ScoringSummary | Record<string, never>;
  validation?: ValidationResult | Record<string, never>;
  gpt_zero?: GptZeroDiagnostic | Record<string, never>;
  baseline_report?: BaselineReport | Record<string, never>;
  /** The baseline run's incomplete-run verdict, for the Stage 8 tiles. */
  baseline_completeness?: RunCompleteness | null;
  blueprint?: Blueprint | Record<string, never>;
  runs?: RunSummary[];
  /** Every rebaseline of the series (drafts and cancelled included). */
  rebaselines?: SeriesRebaseline[];
  /** The battery version the series is read on now (1 = never rebaselined). */
  battery_version?: number;
  schedule?: { items?: ScheduledRemeasurement[] } | Record<string, never>;
  schedule_items?: ScheduledRemeasurement[];
  stage_logs?: any[];
  stage_definitions?: StageDefinition[];
  created_at: string;
  updated_at: string;
}

export interface CustomerSummary {
  id: number;
  brandName: string;
  websiteUrl: string;
  currentStage: number;
  status: string;
  createdAt: string;
  updatedAt: string;
}


// ═══════════════════════════════════════════════════════════════════════════
// REST endpoints — stage definitions, customers, stages 1-9
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchStageDefinitions(): Promise<{ stages: StageDefinition[] }> {
  return apiFetch("/onboarding/stages");
}

export async function listCustomers(opts: {
  status?: string;
  limit?: number;
  offset?: number;
} = {}): Promise<{ items: CustomerSummary[] }> {
  const qs = new URLSearchParams();
  if (opts.status) qs.set("status", opts.status);
  if (opts.limit !== undefined) qs.set("limit", String(opts.limit));
  if (opts.offset !== undefined) qs.set("offset", String(opts.offset));
  return apiFetch(`/onboarding/customers${qs.toString() ? `?${qs}` : ""}`);
}

export async function getCustomer(customerId: number): Promise<CustomerDetail> {
  return apiFetch(`/onboarding/customers/${customerId}`);
}

export interface IntakePayload {
  brand_name: string;
  website_url: string;
  competitors: string[];
  one_liner: string;
  category: string;
  vertical: string;
  brief: string;
}

export async function createCustomer(payload: IntakePayload): Promise<{ customer: CustomerDetail }> {
  return apiFetch("/onboarding/customers", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** Rename a customer's brand. brand_name is the canonical identity key, so the
 *  backend cascades the rename across every table that stores it. */
export async function renameBrand(
  customerId: number,
  brandName: string,
): Promise<{ customer: CustomerDetail }> {
  return apiFetch(`/onboarding/customers/${customerId}/brand-name`, {
    method: "PATCH",
    body: JSON.stringify({ brand_name: brandName }),
  });
}

// ── Stage 2: framework generation (#136) ───────────────────────────────────

export const VERTICAL_OPTIONS = [
  { key: "general", label: "General (core attributes only)" },
  { key: "financial", label: "Financial services" },
];

export type GenerationStepKey =
  | "beliefs" | "category_check" | "anchors" | "personas" | "variants" | "questions";

export const GENERATION_STEPS: GenerationStepKey[] = [
  "beliefs", "category_check", "anchors", "personas", "variants", "questions",
];

export interface ValidationProblem { code: string; path: string; message: string }

export type GenerationCountKey = "beliefs" | "personas" | "questions_per_form";

export interface GenerationStepState {
  step: GenerationStepKey;
  label: string;
  passed_version: number | null;
  passed_step_id: number | null;
  latest_version: number | null;
  latest_status: "passed" | "failed" | null;
  latest_step_id: number | null;
  latest_problems: ValidationProblem[];
  stale_on: string[];
  created_at: string | null;
}

export interface FrameworkGeneration {
  id: number;
  status: "pending" | "running" | "completed" | "failed";
  steps: GenerationStepKey[];
  model: string;
  error: string | null;
  cost_usd: number;
  requested_by: string;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

export interface CategoryFlag { category_default: boolean; reason: string; current: boolean }

export interface FrameworkGenerationOverview {
  available: boolean;
  model: string;
  counts: Record<GenerationCountKey, number>;
  limits: Record<string, { min: number; max: number }>;
  inputs: {
    category: string;
    vertical: string;
    vertical_label: string;
    brief: string;
    competitors: string[];
    pages: number;
    documents: number;
  };
  generated: boolean;
  framework_problems: ValidationProblem[];
  steps: Partial<Record<GenerationStepKey, GenerationStepState>>;
  category_flags: Record<string, CategoryFlag>;
  generation: FrameworkGeneration | null;
  // Framework library templates (#139) the latest generation started from.
  library?: { template_id: number; label: string; match: string }[];
}

export interface GenerationStepDetail {
  id: number;
  step: GenerationStepKey;
  version: number;
  status: string;
  model: string;
  prompt_version: string;
  attempts: { attempt: number; prompt: string; reply?: string; error?: string; problems?: ValidationProblem[] }[];
  inputs: Record<string, any>;
  output: any;
  validation: { passed: boolean; problems: ValidationProblem[] };
  cost_usd: number;
  created_at: string;
  system_prompt: string | null;
  sources: { ref: string | null; url: string; label: string; chars: number; included: boolean; truncated?: boolean }[];
}

/** Start Stage 2 on the worker: every step, or only `steps`. Poll
 *  getFrameworkGeneration for progress. */
export async function runStage2Framework(
  customerId: number,
  body?: { steps?: GenerationStepKey[]; counts?: Partial<Record<GenerationCountKey, number>> },
): Promise<{ customer: CustomerDetail; generation: FrameworkGeneration }> {
  return apiFetch(`/onboarding/customers/${customerId}/stages/2/run`, {
    method: "POST",
    body: JSON.stringify(body ?? {}),
  });
}

export async function getFrameworkGeneration(customerId: number): Promise<FrameworkGenerationOverview> {
  return apiFetch(`/onboarding/customers/${customerId}/framework-generation`);
}

export async function getFrameworkGenerationStep(
  customerId: number, stepId: number,
): Promise<GenerationStepDetail> {
  return apiFetch(`/onboarding/customers/${customerId}/framework-generation/steps/${stepId}`);
}

// ── The question battery (#137) ─────────────────────────────────────────────

export const BATTERY_FORMS: { key: string; label: string }[] = [
  { key: "direct", label: "Direct ask" },
  { key: "problem", label: "Problem" },
  { key: "feature", label: "Feature" },
  { key: "comparison", label: "Comparison" },
  { key: "economics", label: "Economics" },
];

export type LintStatus = "red" | "yellow" | "green";

export interface BatteryLintFinding {
  message: string;
  query_ids: string[];
  persona_id?: string;
  belief_id?: string;
  form?: string;
  unchecked?: boolean;
}

export interface BatteryLintRule {
  rule: string;
  label: string;
  status: LintStatus;
  findings: BatteryLintFinding[];
  fingerprint: string;
  ackable: boolean;
  acknowledged: { rule: string; fingerprint: string; reason: string; by?: string; at?: string } | null;
}

export interface BatteryMatrixCell {
  count: number;
  question_ids: string[];
  excluded: boolean;
  /** The rules this cell fails. */
  failing: string[];
}

export interface BatteryMatrix {
  forms: { id: string; label: string }[];
  personas: { id: string; label: string }[];
  beliefs: { id: string; label: string }[];
  persona_cells: Record<string, Record<string, BatteryMatrixCell>>;
  belief_cells: Record<string, Record<string, BatteryMatrixCell>>;
  persona_totals: Record<string, number>;
  belief_totals: Record<string, number>;
  persona_failing: Record<string, string[]>;
  belief_failing: Record<string, string[]>;
  form_totals: Record<string, number>;
  targets: Record<string, number>;
  unplaced: string[];
  total: number;
}

export interface BatteryLint {
  status: LintStatus;
  /** What approval reads: a red rule, or a yellow one not acknowledged. */
  blocking: boolean;
  /** Held to the linter at approval (generated or carrying a battery block). */
  gated: boolean;
  rules: BatteryLintRule[];
  matrix: BatteryMatrix;
  /** Per question: the category-context verdict, with the words that carry it. */
  context: Record<string, { status: "present" | "missing" | "unchecked"; context?: string; reason?: string }>;
  battery_hash: string;
}

/** The linter over the framework being edited (no model call). */
export async function lintBattery(customerId: number, framework: Framework): Promise<BatteryLint> {
  return apiFetch(`/onboarding/customers/${customerId}/battery/lint`, {
    method: "POST",
    body: JSON.stringify({ framework }),
  });
}

/** Rewrite one persona-by-form cell of the SAVED battery on the worker; poll
 *  getFrameworkGeneration for the generation's status. */
export async function regenerateBatteryCell(
  customerId: number, personaId: string, form: string,
): Promise<{ generation: FrameworkGeneration }> {
  return apiFetch(`/onboarding/customers/${customerId}/battery/cells/regenerate`, {
    method: "POST",
    body: JSON.stringify({ persona_id: personaId, form }),
  });
}

/** The entity resolver's category proposals for a brand at intake. */
export async function proposeCategory(
  brandName: string, websiteUrl: string,
): Promise<{ canonical_name: string; categories: string[]; same_name: { name: string; location: string; domain: string; descriptor: string }[] }> {
  return apiFetch(`/onboarding/intake/propose-category`, {
    method: "POST",
    body: JSON.stringify({ brand_name: brandName, website_url: websiteUrl }),
  });
}

/** Post-intake edit of the brand's facts; each changed field is a
 *  brand_profile_edited MANIFEST event. */
export async function updateCustomerProfile(
  customerId: number,
  changes: { competitors?: string[]; website_url?: string; one_liner?: string; category?: string; brief?: string },
  reason = "",
): Promise<{ customer: CustomerDetail }> {
  return apiFetch(`/onboarding/customers/${customerId}/profile`, {
    method: "PATCH",
    body: JSON.stringify({ ...changes, reason }),
  });
}

/** A query whose variable_ids name reasoning variables the framework does not
 *  have. Approving with any of these is refused by the server; a draft save is
 *  allowed and returns them here. */
export interface StaleVariableMapping {
  query_id: string;
  stale_ids: string[];
}

export async function saveOrApproveFramework(
  customerId: number,
  framework: Framework,
  approve: boolean,
): Promise<{
  customer: CustomerDetail;
  stale_variable_mappings?: StaleVariableMapping[];
  /** The category-context re-check this save ran (#137). */
  battery_check?: { checked: string[]; cost_usd: number; error: string | null };
  /** The framework library refused the approved framework's template (#139).
   *  Leak kinds and counts only. */
  library_warning?: { code: string; message: string; leaks: Record<string, number> } | null;
}> {
  return apiFetch(`/onboarding/customers/${customerId}/stages/3/approve`, {
    method: "POST",
    body: JSON.stringify({ framework, approve }),
  });
}

/**
 * Release the Stage-3 lock so an approved framework can be revised.
 *
 * An approved framework rejects edits (409). This is the supported way to
 * change one: it returns the brand to Stage 3, invalidates the Smoke
 * calibration so the revised rubric must be calibrated again before a
 * baseline, and records the unlock (with `reason`) in the chain of custody.
 */
export async function unlockFramework(
  customerId: number,
  reason: string,
): Promise<{ customer: CustomerDetail }> {
  return apiFetch(`/onboarding/customers/${customerId}/stages/3/unlock`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export async function runStage5Scoring(customerId: number): Promise<{ customer: CustomerDetail }> {
  return apiFetch(`/onboarding/customers/${customerId}/stages/5/run`, { method: "POST" });
}

/**
 * Run Stage-6 validation and advance to Stage 7.
 *
 * `skipHumanReview` records that this run advanced with NO human-review
 * session, so Kappa is stamped as not computed rather than left as an
 * unexplained null. Human review has always been optional to the system and
 * mandatory in the UI; this is how the screen offers what the route already
 * did (customer 14 had to be advanced through the API on 2026-09-12 because
 * there was no button for it).
 */
export async function runStage6(
  customerId: number,
  skipHumanReview = false,
): Promise<{ customer: CustomerDetail }> {
  return apiFetch(`/onboarding/customers/${customerId}/stages/6/run`, {
    method: "POST",
    body: JSON.stringify({ skip_human_review: skipHumanReview }),
  });
}

export async function runStage7(customerId: number): Promise<{ customer: CustomerDetail }> {
  return apiFetch(`/onboarding/customers/${customerId}/stages/7/run`, { method: "POST" });
}

export async function runStage8(customerId: number): Promise<{ customer: CustomerDetail }> {
  return apiFetch(`/onboarding/customers/${customerId}/stages/8/run`, { method: "POST" });
}

export async function runStage9(customerId: number): Promise<{ customer: CustomerDetail }> {
  return apiFetch(`/onboarding/customers/${customerId}/stages/9/run`, { method: "POST" });
}

export async function runStage10BrandMonitoring(
  customerId: number,
  models?: string[],
): Promise<{ customer: CustomerDetail }> {
  return apiFetch(`/onboarding/customers/${customerId}/stages/10/run`, {
    method: "POST",
    body: JSON.stringify(models && models.length ? { models } : {}),
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// Stage 11 — Run history & re-measurements
// ═══════════════════════════════════════════════════════════════════════════

export async function listRuns(customerId: number): Promise<{ runs: RunSummary[] }> {
  return apiFetch(`/onboarding/customers/${customerId}/runs`);
}

export interface StartReRunRequest {
  batch_config: {
    queries: number;
    reps: number;
    models: string[];
    // "baseline" (versions the imported baseline ran with) or "default" (current
    // live versions). Omitted → backend defaults to "baseline".
    version_mode?: "baseline" | "default";
    // Optional per-lane model overrides: { lane: version }. Backend re-validates.
    model_overrides?: Record<string, string>;
    // Optional Stage-5 scorer (judge) model. A re-measurement auto-scores through
    // the robust Stage-5 path as soon as collection finishes — before the manual
    // Stage-5 picker is reachable — so pin the judge here to reproduce a baseline
    // scored on a non-default model. Omitted → backend registry default.
    scorer_model_id?: string;
    // Optional per-lane web-search selection: { lane: boolean }. Whether each
    // provider is probed web-grounded (true) or static (false). Sparse — a lane
    // omitted falls back to the catalog default, so existing behaviour is
    // unchanged. Backend resolves the full map and stores it on the run.
    web_search_overrides?: Record<string, boolean>;
    // Optional per-lane endpoint overrides (#90): { lane: "sonar" | "agent" }.
    // Today only meaningful for perplexity (Sonar chat completions, retiring
    // 2026-09-27, vs. the Agent API). A lane absent here runs its catalog
    // default_endpoint.
    endpoint_overrides?: Record<string, string>;
  };
  /** Optional free-text display name for the new run. */
  run_name?: string;
  /** A pre-flight / de-risking probe rather than a measurement. Recorded with
   *  series_inclusion="exclude" at creation, so it can never become a point on a
   *  client-facing Performance chart whatever its run_label says — the switch
   *  that did not exist when a 6-row probe launched as a "re-measurement"
   *  spiked customer 8's chart. */
  preflight?: boolean;
}

export async function startReRun(
  customerId: number,
  body: StartReRunRequest,
): Promise<{ run: RunSummary }> {
  return apiFetch(`/onboarding/customers/${customerId}/runs`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Set or clear a run's free-text display name. Empty string clears it. */
export async function renameRun(
  customerId: number,
  runId: number,
  runName: string,
): Promise<{ run: RunSummary }> {
  return apiFetch(`/onboarding/customers/${customerId}/runs/${runId}/name`, {
    method: "PATCH",
    body: JSON.stringify({ run_name: runName }),
  });
}

/** Set how a run participates in the Performance time series (display-only —
 *  the run's data is untouched).
 *
 *    "auto"    hand it back to the rules: it plots only if it is a real
 *              measurement whose scored-row count clears the floor.
 *    "exclude" never plot it (a discarded re-measure, or a pre-flight probe).
 *    "include" plot it even though the floor would fence it out — the deliberate
 *              override, and the only way a small run reaches a client chart.
 *
 *  `reason` is recorded on the run's MANIFEST event, so the chain of custody says
 *  why a run was taken off (or put onto) a client chart. */
export async function setRunSeriesInclusion(
  customerId: number,
  runId: number,
  inclusion: "auto" | "include" | "exclude",
  reason = "",
): Promise<{ run: RunSummary }> {
  return apiFetch(
    `/onboarding/customers/${customerId}/runs/${runId}/series-exclusion`,
    {
      method: "PATCH",
      body: JSON.stringify({ inclusion, reason }),
    },
  );
}

/** Legacy two-state helper. Un-excluding means "let the rules decide again"
 *  ("auto"), not "force it onto the chart" — use setRunSeriesInclusion for that. */
export async function setRunSeriesExclusion(
  customerId: number,
  runId: number,
  excluded: boolean,
  reason = "",
): Promise<{ run: RunSummary }> {
  return setRunSeriesInclusion(customerId, runId, excluded ? "exclude" : "auto", reason);
}

export async function getRunDetail(
  customerId: number,
  runId: number,
): Promise<{ run: RunDetail }> {
  return apiFetch(`/onboarding/customers/${customerId}/runs/${runId}`);
}

export async function compareRuns(
  customerId: number,
  baselineRunId: number,
  candidateRunId: number,
): Promise<RunComparison> {
  const qs = new URLSearchParams({
    baseline: String(baselineRunId),
    candidate: String(candidateRunId),
  });
  return apiFetch(`/onboarding/customers/${customerId}/runs/compare/data?${qs}`);
}

/** Cancel a running or pending measurement run. */
export async function cancelRun(runId: number): Promise<{ run: any }> {
  return apiFetch(`/onboarding/measurement/${runId}/cancel`, { method: "POST" });
}

/**
 * (Re)run the scoring → validation → report pipeline for a completed run as a
 * background task. Used by the "Run scoring / regenerate report" button when a
 * run finished collection but its scoring/report never completed. Reuses
 * existing scoring when present (report-only regen).
 */
export async function runRunPipeline(
  customerId: number,
  runId: number,
): Promise<{ run: RunSummary }> {
  return apiFetch(`/onboarding/customers/${customerId}/runs/${runId}/pipeline/run`, {
    method: "POST",
  });
}


// ═══════════════════════════════════════════════════════════════════════════
// File downloads (PDF + log/data exports)
// ═══════════════════════════════════════════════════════════════════════════
//
// apiFetch parses responses as JSON/text; downloads need raw bytes. We still
// use buildApiUrl so requests reach FastAPI rather than Vite's SPA fallback
// (the historical bug that saved index.html as a .pdf). The PDF path keeps a
// strict %PDF- header sniff; the generic path validates content-type loosely
// so it works for json/csv/html.

function buildExportUrl(path: string): string {
  const base = buildApiUrl();
  if (!base) return path;
  if (path.startsWith("http")) return path;
  return `${base}${path.startsWith("/") ? "" : "/"}${path}`;
}

function authHeader(): Record<string, string> {
  const token = getStoredToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function triggerBlobDownload(blob: Blob, filename: string) {
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
}

/**
 * Generic authenticated file download for non-PDF exports (json/csv/html).
 * Honours the Content-Disposition filename from the server when present and
 * falls back to `fallbackFilename` otherwise. Exported so the scoring API
 * client can reuse it.
 */
export async function downloadExportFile(
  path: string,
  fallbackFilename: string,
  accept = "application/octet-stream",
  method: "GET" | "POST" = "GET",
): Promise<void> {
  const url = buildExportUrl(path);

  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers: { ...authHeader(), Accept: accept },
    });
  } catch (e: any) {
    throw new Error(
      e?.message ||
        `Network request failed for ${url}. Check API base URL and connectivity.`,
    );
  }

  if (res.status === 401) {
    notifyAuthExpired(path);
    throw new AuthExpiredError(path);
  }
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`Download failed (${res.status}): ${txt.slice(0, 200)}`);
  }

  // Reject the Vite SPA fallback (an HTML page where data was expected).
  const ct = (res.headers.get("content-type") || "").toLowerCase();
  if (accept !== "text/html" && ct.includes("text/html")) {
    const preview = await res.text().catch(() => "");
    throw new Error(
      `Expected a data export, got HTML. URL: ${url}. ` +
        `Body starts with: ${preview.slice(0, 120).replace(/\s+/g, " ")}`,
    );
  }

  const dispo = res.headers.get("content-disposition") || "";
  const m = /filename="?([^"]+)"?/i.exec(dispo);
  const filename = m?.[1] || fallbackFilename;

  const buf = await res.arrayBuffer();
  if (buf.byteLength === 0) throw new Error("Server returned an empty file");
  triggerBlobDownload(new Blob([buf], { type: ct || accept }), filename);
}

async function downloadPdf(path: string, suggestedFilename: string): Promise<void> {
  const url = buildExportUrl(path);

  let res: Response;
  try {
    res = await fetch(url, {
      method: "GET",
      headers: { ...authHeader(), Accept: "application/pdf" },
    });
  } catch (e: any) {
    throw new Error(
      e?.message ||
        `Network request failed for ${url}. Check API base URL, CORS, and connectivity.`,
    );
  }

  if (res.status === 401) {
    notifyAuthExpired(path);
    throw new AuthExpiredError(path);
  }
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`Download failed (${res.status}): ${txt.slice(0, 200)}`);
  }

  const contentType = res.headers.get("content-type") || "";
  if (!contentType.toLowerCase().includes("application/pdf")) {
    const preview = await res.text().catch(() => "");
    throw new Error(
      `Expected application/pdf, got "${contentType || "unknown"}". URL: ${url}. ` +
        `Body starts with: ${preview.slice(0, 120).replace(/\s+/g, " ")}`,
    );
  }

  const arrayBuffer = await res.arrayBuffer();
  if (arrayBuffer.byteLength < 5) {
    throw new Error("Server returned an empty or truncated PDF");
  }
  const head = new Uint8Array(arrayBuffer.slice(0, 5));
  const isPdf =
    head[0] === 0x25 && head[1] === 0x50 && head[2] === 0x44 &&
    head[3] === 0x46 && head[4] === 0x2d;
  if (!isPdf) {
    throw new Error(
      `Server response is not a valid PDF (missing %PDF- header). ` +
        `Got bytes: ${Array.from(head).map((b) => b.toString(16).padStart(2, "0")).join(" ")}`,
    );
  }

  triggerBlobDownload(
    new Blob([arrayBuffer], { type: "application/pdf" }),
    `${suggestedFilename}.pdf`,
  );
}

const MD_FILENAMES: Record<string, string> = {
  framework: "framework",
  baseline: "baseline-report",
  blueprint: "intervention-blueprint",
};

const PDF_FILENAMES: Record<string, string> = {
  framework: "framework",
  baseline: "baseline-report",
  blueprint: "intervention-blueprint",
};

type CustomerLike = number | { id: number };

function _customerId(c: CustomerLike): number {
  return typeof c === "number" ? c : c.id;
}

export async function downloadFrameworkPdf(customer: CustomerLike): Promise<void> {
  const id = _customerId(customer);
  return downloadPdf(
    `/onboarding/customers/${id}/exports/framework.pdf`,
    `${PDF_FILENAMES.framework}-customer-${id}`,
  );
}

export async function downloadBaselinePdf(customer: CustomerLike): Promise<void> {
  const id = _customerId(customer);
  return downloadPdf(
    `/onboarding/customers/${id}/exports/baseline.pdf`,
    `${PDF_FILENAMES.baseline}-customer-${id}`,
  );
}

export async function downloadBlueprintPdf(customer: CustomerLike): Promise<void> {
  const id = _customerId(customer);
  return downloadPdf(
    `/onboarding/customers/${id}/exports/blueprint.pdf`,
    `${PDF_FILENAMES.blueprint}-customer-${id}`,
  );
}

export async function downloadFrameworkMd(customer: CustomerLike): Promise<void> {
  const id = _customerId(customer);
  return downloadExportFile(
    `/onboarding/customers/${id}/exports/framework.md`,
    `${MD_FILENAMES.framework}-customer-${id}.md`,
    "text/markdown",
  );
}

export async function downloadBaselineMd(customer: CustomerLike): Promise<void> {
  const id = _customerId(customer);
  return downloadExportFile(
    `/onboarding/customers/${id}/exports/baseline.md`,
    `${MD_FILENAMES.baseline}-customer-${id}.md`,
    "text/markdown",
  );
}

export async function downloadBlueprintMd(customer: CustomerLike): Promise<void> {
  const id = _customerId(customer);
  return downloadExportFile(
    `/onboarding/customers/${id}/exports/blueprint.md`,
    `${MD_FILENAMES.blueprint}-customer-${id}.md`,
    "text/markdown",
  );
}

/** Per-run report PDF — used by the Stage 11 run-history rows. */
export async function downloadRunReportPdf(
  customerId: number,
  runId: number,
  runSequenceNumber?: number | null,
): Promise<void> {
  const label = runSequenceNumber ? `run-${runSequenceNumber}` : `run-${runId}`;
  return downloadPdf(
    `/onboarding/customers/${customerId}/runs/${runId}/exports/report.pdf`,
    `${label}-customer-${customerId}`,
  );
}

/** Per-run report as Markdown: the read's report content, including the
 *  belief block an admin chose for this read on the Deep Signal belief page. */
export async function downloadRunReportMd(
  customerId: number,
  runId: number,
  runSequenceNumber?: number | null,
): Promise<void> {
  const label = runSequenceNumber ? `run-${runSequenceNumber}` : `run-${runId}`;
  return downloadExportFile(
    `/onboarding/customers/${customerId}/runs/${runId}/exports/report.md`,
    `${label}-customer-${customerId}.md`,
    "text/markdown",
  );
}

/**
 * Stage 4 — download the raw query + response log for a measurement run.
 * `format` "json" keeps full fidelity (batch config, status, cost);
 * "csv" is one row per response for spreadsheet review.
 */
export async function downloadRunResponses(
  customerId: number,
  runId: number,
  format: "json" | "csv" = "csv",
  runSequenceNumber?: number | null,
): Promise<void> {
  const label = runSequenceNumber ? `run-${runSequenceNumber}` : `run-${runId}`;
  return downloadExportFile(
    `/onboarding/customers/${customerId}/runs/${runId}/exports/responses?format=${format}`,
    `stage4-responses-${label}-customer-${customerId}.${format}`,
    format === "json" ? "application/json" : "text/csv",
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// WebSockets — measurement progress + auto-pipeline events
// ═══════════════════════════════════════════════════════════════════════════

export type MeasurementWsMessage =
  | { type: "snapshot"; run: any }
  | { type: "no_run" }
  | { type: "measurement_started"; phase: string; total: number; config: any; run_id: number; run_sequence_number?: number; timestamp: string }
  | { type: "measurement_progress"; phase: string; completed: number; total: number; percent: number; cost_so_far: number; run_id: number; timestamp: string }
  | { type: "measurement_finished"; phase: string; completed: number; total: number; cost_usd: number; run_id: number; summary: any; timestamp: string }
  | { type: "auto_pipeline_started"; phase: string; run_id: number; timestamp: string }
  | { type: "auto_pipeline_failed"; phase: string; run_id: number; message: string; timestamp: string }
  // Stage 11 re-runs mirror the robust Stage-5 scoring lifecycle onto the
  // measurement channel so the run-history row can show live scoring progress.
  | { type: "scoring_started"; phase?: string; run_id: number; total: number; timestamp?: string }
  | { type: "scoring_progress"; phase?: string; run_id: number; completed: number; total: number; percent: number; cost_so_far: number; timestamp?: string }
  | { type: "measurement_complete"; phase: string; completed: number; total: number; cost_usd: number; run_id: number; auto_pipeline_failed?: boolean; auto_pipeline_error?: string; scoring_summary?: any; validation?: any; baseline_report?: any; summary: any; customer: any; timestamp: string }
  | { type: "measurement_cancelled"; phase: string; completed: number; total: number; cost_usd: number; run_id: number; timestamp: string }
  | { type: "heartbeat"; phase?: string; run_id?: number; timestamp: string }
  | { type: "error"; message: string; trace?: string; phase?: string; run_id?: number; timestamp: string };

interface OpenMeasurementWsOpts {
  customerId: number;
  runId?: number;
  onEvent: (msg: MeasurementWsMessage) => void;
  onClose?: () => void;
  onError?: (e: Event) => void;
}

export function openMeasurementWs(opts: OpenMeasurementWsOpts): WebSocket {
  const token = getStoredToken();
  if (!token) {
    notifyAuthExpired("/onboarding/ws/measurement");
    throw new AuthExpiredError("/onboarding/ws/measurement");
  }
  const base = buildWsUrl();
  const params = new URLSearchParams({
    token,
    customer_id: String(opts.customerId),
  });
  if (opts.runId !== undefined) params.set("run_id", String(opts.runId));

  const url = `${base}/onboarding/ws/measurement?${params.toString()}`;
  const ws = new WebSocket(url);

  ws.onmessage = (e) => {
    try {
      opts.onEvent(JSON.parse(e.data));
    } catch {
      /* ignore malformed */
    }
  };
  if (opts.onClose) ws.onclose = opts.onClose;
  if (opts.onError) ws.onerror = opts.onError;
  return ws;
}

export type ScoringWsMessage =
  | { type: "scoring_started"; total: number; timestamp?: string }
  | { type: "scoring_progress"; completed: number; total: number; timestamp?: string }
  | { type: "scoring_complete"; customer: CustomerDetail }
  | { type: "error"; message: string };

/** Legacy alias — older code imports WsMessage instead of ScoringWsMessage. */
export type WsMessage = ScoringWsMessage | MeasurementWsMessage;

interface OpenScoringWsOpts {
  customerId: number;
  onEvent: (msg: ScoringWsMessage) => void;
  onClose?: () => void;
  onError?: (e: Event) => void;
}

export function openScoringWs(opts: OpenScoringWsOpts): WebSocket {
  const token = getStoredToken();
  if (!token) {
    notifyAuthExpired("/onboarding/ws/scoring");
    throw new AuthExpiredError("/onboarding/ws/scoring");
  }
  const base = buildWsUrl();
  const params = new URLSearchParams({
    token,
    customer_id: String(opts.customerId),
  });

  const url = `${base}/onboarding/ws/scoring?${params.toString()}`;
  const ws = new WebSocket(url);

  ws.onmessage = (e) => {
    try {
      opts.onEvent(JSON.parse(e.data));
    } catch {
      /* ignore */
    }
  };
  if (opts.onClose) ws.onclose = opts.onClose;
  if (opts.onError) ws.onerror = opts.onError;
  return ws;
}